import { useTranslation } from 'react-i18next';

import {
  NotificationHubFilter,
  useHasNotificationHubFilters,
} from '@/features/notifications/components/notification-hub-filter';
import { NotificationsTable } from '@/features/notifications/components/notifications-table';
import { useNotifications } from '@/features/notifications/hooks/use-notifications';
import { NotificationReadStatusButton } from '@/features/notifications/notification-read-status-button';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { EmptyTablePlaceholder } from '@/shared/components/table/empty-table-placeholder';
import { Button } from '@/shared/components/ui/button';
import { useLoadMore } from '@/shared/hooks/use-load-more';

const PAGE_SIZE = 10;

export const NotificationHubPage = () => {
  const { data: notifications, isLoading, isError } = useNotifications();
  const { t } = useTranslation('notification', {
    keyPrefix: 'NotificationHub',
  });
  const hasFilters = useHasNotificationHubFilters();

  // unreadCount is computed from the full array, not the paginated slice
  const unreadCount =
    notifications?.reduce(
      (count, notification) => count + (notification.is_read ? 0 : 1),
      0,
    ) ?? 0;

  const { visibleData, hasMore, loadMore } = useLoadMore({
    data: notifications ?? [],
    pageSize: PAGE_SIZE,
  });

  return (
    <DefaultLayout
      title={t('title')}
      headerActions={[
        notifications && notifications.length > 0 && unreadCount > 0 && (
          <NotificationReadStatusButton notifications={notifications} />
        ),
        <NotificationHubFilter />,
      ]}
    >
      <UnreadMessagesStatus unreadCount={unreadCount} />
      {isLoading ? (
        <LoadingIndicator />
      ) : !notifications || isError ? (
        <span>{t('Errors.fetch')}</span>
      ) : notifications.length === 0 ? (
        <EmptyTablePlaceholder
          title={
            hasFilters ? t('no_notifications_found') : t('no_notifications')
          }
          description={
            hasFilters ? undefined : t('no_notifications_description')
          }
          columns={['']}
          centered
          dottedBorder
          withHeader={false}
        />
      ) : (
        <>
          <NotificationsTable notifications={visibleData} />
          {hasMore && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={loadMore}>
                {t('load_more')}
              </Button>
            </div>
          )}
        </>
      )}
    </DefaultLayout>
  );
};

const UnreadMessagesStatus = ({ unreadCount }: { unreadCount: number }) => {
  const { t } = useTranslation('notification', {
    keyPrefix: 'NotificationHub',
  });

  if (unreadCount === 0) return undefined;

  return (
    <div className="mb-9 flex items-center gap-2 text-2xs">
      {t('unread_notifications')}
      <div className="flex size-6 items-center justify-center rounded-full bg-white text-2xs font-medium text-black">
        {unreadCount}
      </div>
    </div>
  );
};
